/** Adapter between the API campaign (raw Prisma + computed totals) and the UI `Campaign`. */

import type {
  Campaign,
  CampaignDailyResult,
  CampaignObjective,
  CampaignReport,
  CampaignStatus,
  Platform,
} from "@/types";

export interface ApiCampaign {
  id: string;
  name: string;
  clientId: string;
  platform: Platform;
  objective: CampaignObjective;
  totalBudget?: string | number | null;
  startDate?: string | null;
  endDate?: string | null;
  status: CampaignStatus;
  stopReason?: string | null;
  note?: string | null;
  client?: { id: string; name: string } | null;
  owner?: { id: string; name: string } | null;
  totalSpentUsd?: number;
  totalResults?: number;
  cpr?: number | null;
}

export interface ApiCampaignReport {
  campaign: ApiCampaign;
  daily: Array<{
    id: string;
    date: string;
    spentUsd: number;
    resultsCount: number;
    cpr: number | null;
    note?: string | null;
  }>;
  totals: { spentUsd: number; results: number; cpr: number | null; days: number };
}

export interface CampaignInput {
  name: string;
  clientId: string;
  platform: Platform;
  objective: CampaignObjective;
  totalBudget?: number;
  startDate?: string;
  endDate?: string;
  note?: string;
}

function toNumber(value: string | number | null | undefined): number | undefined {
  if (value === null || value === undefined) return undefined;
  const n = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(n) ? n : undefined;
}

function formatDate(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

export function toCampaign(api: ApiCampaign): Campaign {
  return {
    id: api.id,
    name: api.name,
    clientId: api.clientId,
    clientName: api.client?.name ?? "—",
    platform: api.platform,
    objective: api.objective,
    totalBudget: toNumber(api.totalBudget),
    startDate: formatDate(api.startDate),
    endDate: formatDate(api.endDate),
    ownerName: api.owner?.name ?? undefined,
    status: api.status,
    stopReason: api.stopReason ?? undefined,
    note: api.note ?? undefined,
    totalSpentUsd: api.totalSpentUsd ?? 0,
    totalResults: api.totalResults ?? 0,
    cpr: api.cpr ?? null,
  };
}

export function toCampaignReport(api: ApiCampaignReport): CampaignReport {
  const daily: CampaignDailyResult[] = api.daily.map((d) => ({
    id: d.id,
    date: formatDate(d.date) ?? d.date,
    spentUsd: d.spentUsd,
    resultsCount: d.resultsCount,
    cpr: d.cpr,
    note: d.note ?? undefined,
  }));
  return { daily, totals: api.totals };
}

export function toCampaignWriteBody(input: CampaignInput): Record<string, unknown> {
  return {
    name: input.name,
    clientId: input.clientId,
    platform: input.platform,
    objective: input.objective,
    ...(input.totalBudget !== undefined ? { totalBudget: input.totalBudget } : {}),
    ...(input.startDate ? { startDate: input.startDate } : {}),
    ...(input.endDate ? { endDate: input.endDate } : {}),
    ...(input.note ? { note: input.note } : {}),
  };
}
